import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhone } from "@WhiteCap/Common/Utillities/utils";
import CustomCloseIcon from "@WhiteCap/Icons/CustomCloseIcon";
import CustomEditIcon from "@WhiteCap/Icons/CustomEditIcon";
import CustomTickIcon from "@WhiteCap/Icons/CustomTickIcon";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { SubscribedEmailDto } from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ManageSubscribersStyles;
    subscribersCount?: number | string;
    emails?: SubscribedEmailDto[];
    removeEmail: (emailId: string) => void;
    updateSubscriberDetails: (
        email: string,
        phoneNo: string,
        isSubscribedToReceiveSMS: boolean,
        id?: string,
        isActive?: boolean,
        isEditing?: boolean,
    ) => void;
    removePhoneNumber: (email: string, onRemovePhoneSuccessCallback: () => void) => void;
    isPodReportingModal?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomApplicationState) => ({
    isFetchingSubscribersDetails: state.WhiteCap.subscriptionsDashboardData.isFetchingSubscribers,
    emailForWhichPhoneNumberIsBeingFetched:
        state.WhiteCap.subscriptionsDashboardData.emailForWhichPhoneNumberIsBeingFetched,
});

export interface ManageSubscribersStyles {
    container?: InjectableCss;
    sectionHeading?: TypographyProps;
    wrapper?: InjectableCss;
    iconBtn?: ButtonIconPresentationProps;
    email?: TypographyProps;
    cardContainer?: InjectableCss;
    header?: InjectableCss;
    checkboxContainer?: InjectableCss;
    checkboxLabel?: TypographyProps;
    standardCharges?: TypographyProps;
    checkbox?: CheckboxPresentationProps;
    checkboxGroup?: CheckboxGroupPresentationProps;
    closeBtn?: ButtonPresentationProps;
    closeIcon?: IconPresentationProps;
    phoneSection?: InjectableCss;
    phoneField?: TextFieldPresentationProps;
    saveBtn?: ButtonPresentationProps;
    editBtn?: ButtonPresentationProps;
    removeBtn?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    emailWrapper?: InjectableCss;
    badge?: InjectableCss;
    disclaimer?: TypographyProps;
    disclaimerContainer?: InjectableCss;
}

export const manageSubscribersStyles: ManageSubscribersStyles = {
    container: {},
    sectionHeading: {
        variant: "h4",
        weight: 325,
        lineHeight: "normal",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            margin-bottom: 8px;
                        `,
                        null,
                        css`
                            font-size: 24px;
                            margin-bottom: 21px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            gap: 22px;
            flex-direction: column;
        `,
    },
    email: {
        weight: 500,
    },
    cardContainer: {
        css: css`
            background: #f7f8f9;
            padding: 21px;
            width: 100%;
            margin-bottom: 13px;
        `,
    },
    header: {
        css: css`
            display: flex;
            gap: 20px;
            justify-content: space-between;
        `,
    },
    checkbox: {
        css: css`
            gap: 8px;
            svg {
                color: ${getColor("text.main")};
            }
        `,
    },
    checkboxGroup: {
        mergeCss: true,
        css: css`
            [class^="TypographyStyle"] {
                margin: 0 !important;
            }
        `,
    },
    checkboxContainer: {
        css: css`
            margin-top: 11px;
            display: flex;
            flex-direction: column;
            gap: 11px;
        `,
    },
    checkboxLabel: {
        css: css`
            weight: 325;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                        `,
                        null,
                        css`
                            font-size: 14px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    standardCharges: {
        css: css`
            weight: 325;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 10px;
                        `,
                        null,
                        css`
                            font-size: 14px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    closeBtn: {
        variant: "primary",
        css: css`
            min-height: fit-content;
            height: fit-content;
            padding: 4px 4px;
        `,
    },
    iconBtn: {
        color: "text.main",
        size: 16,
        css: css`
            height: 16px;
            width: 16px;
            svg {
                color: ${getColor("text.main")};
            }
        `,
    },
    phoneSection: {
        css: css`
            display: flex;
            gap: 10px;
            width: 100%;
        `,
    },
    phoneField: {
        cssOverrides: {
            formField: css`
                flex-grow: 1;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                max-width: 200px;
                                margin-left: 24px;
                            `,
                        ],
                        "min",
                    )}
            `,
            inputSelect: css`
                &:disabled {
                    color: ${getColor("text.main")};
                    background: #f7f8f9;
                }
            `,
        },
    },
    editBtn: {
        css: css`
            padding: 10px;
            height: 40px;
            width: 40px;
            [class^="IconWrapper"] {
                position: relative;
                top: -0.1em;
                left: -0.1em;
                width: 20px;
                height: 20px;
                svg {
                    width: 20px;
                    height: 20px;
                }
            }
            background: white;
        `,
    },
    saveBtn: {
        css: css`
            height: 40px;
            width: 40px;
            padding: 10px;
            [class^="IconWrapper"] {
                position: relative;
                top: -0.125em;
                left: -0.125em;
                width: 20px;
                height: 20px;
                svg {
                    width: 20px;
                    height: 20px;
                }
            }
            background: white;
        `,
    },
    removeBtn: {
        css: css`
            height: 40px;
            width: 40px;
            padding: 12px;
            [class^="IconWrapper"] {
                position: relative;
                top: -0.125em;
                left: -0.125em;
                width: 16px;
                height: 16px;
                svg {
                    width: 16px;
                    height: 16px;
                }
            }
            background: white;
        `,
    },
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 200px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    emailWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 10px;
        `,
    },
    badge: {
        css: css`
            text-align: center;
            padding: 4px 12px;
            color: #fffff;
        `,
    },
    disclaimerContainer: {
        css: css`
            line-height: normal;
            margin-top: 3px;
        `,
    },
    disclaimer: {
        size: 10,
        color: "text.main",
        lineHeight: "16px",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-left: 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = manageSubscribersStyles;

const ManageSubscribers = ({
    subscribersCount,
    emails,
    removeEmail,
    updateSubscriberDetails,
    isFetchingSubscribersDetails,
    removePhoneNumber,
    emailForWhichPhoneNumberIsBeingFetched,
    isPodReportingModal,
}: Props) => {
    if (!subscribersCount || subscribersCount === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.sectionHeading}>Manage Subscribers ({subscribersCount})</Typography>
            {isFetchingSubscribersDetails ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.wrapper}>
                    {emails?.map((email: SubscribedEmailDto) => {
                        return (
                            <EmailCard
                                email={email}
                                key={email.email}
                                removeEmail={removeEmail}
                                updateSubscriberDetails={updateSubscriberDetails}
                                removePhoneNumber={removePhoneNumber}
                                emailForWhichPhoneNumberIsBeingFetched={emailForWhichPhoneNumberIsBeingFetched}
                                isPodReportingModal={isPodReportingModal}
                            />
                        );
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ManageSubscribers);

type EmailCardProps = {
    email: SubscribedEmailDto;
    removeEmail: (id: string) => void;
    updateSubscriberDetails: (
        email: string,
        phoneNo: string,
        isSubscribedToReceiveSMS: boolean,
        id?: string,
        isActive?: boolean,
        isEditing?: boolean,
    ) => void;
    removePhoneNumber: (email: string, onRemovePhoneSuccessCallback: () => void) => void;
    emailForWhichPhoneNumberIsBeingFetched: string;
    isPodReportingModal?: boolean;
};

const EmailCard = ({
    email,
    removeEmail,
    updateSubscriberDetails,
    removePhoneNumber,
    emailForWhichPhoneNumberIsBeingFetched,
    isPodReportingModal,
}: EmailCardProps) => {
    const phoneNotValidErrorMessage = siteMessage("OSN_Invalid_Phone_Message");
    const phoneRequiredErrorMessage = siteMessage("OSN_Phone_Required_Message");
    const disclaimerMsg = siteMessage("OSN_Phone_Disclaimer_Message");
    const [phone, setPhone] = useState(email.phone ? formatPhone(email.phone) : "");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [phoneError, setPhoneError] = useState(phoneRequiredErrorMessage);

    useEffect(() => {
        setPhone(email.phone ? formatPhone(email.phone) : "");
    }, [email.phone]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        const regex = new RegExp("^[0-9]*$");
        if (!value) {
            setPhoneError(phoneRequiredErrorMessage);
            setPhone("");
            return;
        }
        value = value.split("-").join("");
        if (value.length <= 10 && regex.test(value)) {
            const formattedPhone = formatPhone(value);
            value.length === 10 ? setPhoneError("") : setPhoneError(phoneNotValidErrorMessage);
            setPhone(formattedPhone);
        }
    };

    const savePhone = () => {
        setIsSubmitted(true);
        if (phone && phone === formatPhone(email.phone)) {
            // If updated phone number and exisiting phone are same
            updateSubscriberDetails(email.email, email.phone, true, email.id, email.isActive || false, false);
            return;
        }

        if (phoneError) {
            return;
        }
        const phoneNumber = phone.split("-").join("");
        // Update subscriber details & set editing to false.
        updateSubscriberDetails(email.email, phoneNumber, true, email.id, email.isActive || false, false);
    };

    const onRemovePhoneSuccessCallback = () => {
        // Restore to initial state
        setPhone("");
        setPhoneError(phoneRequiredErrorMessage);
        setIsSubmitted(false);
    };

    const handleRemovePhone = (emailId: string) => {
        removePhoneNumber(emailId, onRemovePhoneSuccessCallback);
    };

    const phoneSection = (
        <>
            <StyledWrapper {...styles.phoneSection}>
                <TextField
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter Phone Number"
                    error={isSubmitted && (!email.phone || phone !== formatPhone(email.phone)) && phoneError}
                    {...styles.phoneField}
                    disabled={!email.isEditing}
                    onKeyPress={e => {
                        if (e.key === "Enter") {
                            savePhone();
                        }
                    }}
                />
                {email.isEditing && (
                    <Button {...styles.saveBtn} variant="secondary" onClick={() => savePhone()}>
                        <ButtonIcon size={20} src={CustomTickIcon} {...styles.iconBtn} />
                    </Button>
                )}

                {email.phone && !email.isEditing && (
                    <>
                        <Button
                            {...styles.editBtn}
                            variant="secondary"
                            onClick={() => {
                                // Set is editing true
                                updateSubscriberDetails(
                                    email.email,
                                    email.phone,
                                    true,
                                    email.id,
                                    email.isActive || false,
                                    true,
                                );
                            }}
                        >
                            <ButtonIcon size={20} src={CustomEditIcon} {...styles.iconBtn} />
                        </Button>
                        <Button
                            {...styles.removeBtn}
                            variant="secondary"
                            onClick={() => handleRemovePhone(email.email)}
                        >
                            <ButtonIcon src={CustomCloseIcon} {...styles.iconBtn} />
                        </Button>
                    </>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.disclaimerContainer}>
                <Typography {...styles.disclaimer}>{disclaimerMsg}</Typography>
            </StyledWrapper>
        </>
    );

    const handleSubscribeToReceiveSMS = (value: boolean) => {
        if (value === true && !email.phone) {
            updateSubscriberDetails(email.email, email.phone, value, email.id, email.isActive || false, true);
            setPhone(formatPhone(email.phone));
        } else {
            // If phone number already exists don't open editing state
            updateSubscriberDetails(email.email, email.phone, value, email.id, email.isActive || false, false);
        }
        // Set phone number as existing number if subscribe to receive SMS is checked/unchecked
        setPhone(formatPhone(email.phone));
    };

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={email.email === emailForWhichPhoneNumberIsBeingFetched}>
            <StyledWrapper {...styles.cardContainer}>
                <StyledWrapper {...styles.header}>
                    <StyledWrapper {...styles.emailWrapper}>
                        <Typography {...styles.email}>{email.email}</Typography>
                        {/* {!email.isActive && (
                            <Badge
                                type={BadgeTypeValues.Text}
                                value="Unsubscribed"
                                badgeBackgroundColor="E64E25"
                                badgeTextRounding={15}
                                textBadgeCss={styles.badge}
                            />
                        )} */}
                    </StyledWrapper>
                    <Button {...styles.closeBtn} onClick={() => removeEmail(email.email)}>
                        <ButtonIcon src={X} {...styles.iconBtn} />
                    </Button>
                </StyledWrapper>
                {
                    !isPodReportingModal && <StyledWrapper {...styles.checkboxContainer}>
                    <CheckboxGroup {...styles.checkboxGroup}>
                        <Checkbox
                            onChange={(e, value) => handleSubscribeToReceiveSMS(value)}
                            checked={email.isSubscribedToReceiveSMS}
                            {...styles.checkbox}
                        >
                            <Typography {...styles.checkboxLabel}>
                                Send SMS Notification {` `}
                                <Typography {...styles.standardCharges}>(standard charges apply)</Typography>
                            </Typography>
                        </Checkbox>
                    </CheckboxGroup>
                    {!email.isSubscribedToReceiveSMS ? null : phoneSection}
                </StyledWrapper>
                }
                
            </StyledWrapper>
        </LoadingOverlay>
    );
};
